import React from 'react'
import { Box, Skeleton, Typography } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import { AlertTradeSummaryStats } from '../types/AlertHistoryItem'
import { formatAlertProfit } from '../utils/alertFormatters'
import AlertCardSectionTitle from './AlertCardSectionTitle'

interface StatProps {
  label: string
  value: React.ReactNode
  color?: string
}

const Stat: React.FC<StatProps> = ({ label, value, color }) => {
  const theme = useTheme()
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 0 }}>
      <Typography
        noWrap
        sx={{ fontSize: '14px', fontWeight: 700, color: color || theme.palette.text.primary }}
      >
        {value}
      </Typography>
      <Typography sx={{ fontSize: '10px', color: theme.palette.text.secondary }}>
        {label}
      </Typography>
    </Box>
  )
}

interface AccountPerformanceProps {
  stats: AlertTradeSummaryStats | null
  loading: boolean
  onTitleClick: () => void
}

// Aggregate results of every trade this alert has placed on its auto-trade account
const AccountPerformance: React.FC<AccountPerformanceProps> = ({
  stats,
  loading,
  onTitleClick,
}) => {
  const theme = useTheme()

  const netColor =
    !stats || stats.netProfit === 0
      ? theme.palette.text.primary
      : stats.netProfit > 0
        ? theme.palette.success.main
        : theme.palette.error.main
  const winRate =
    stats && stats.closedCount > 0
      ? `${Math.round((stats.winCount / stats.closedCount) * 100)}%`
      : '—'

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      <AlertCardSectionTitle
        title="Account Performance"
        onClick={onTitleClick}
        endIcon={<ChevronRightIcon sx={{ fontSize: 16 }} />}
      />
      {loading && !stats ? (
        <Skeleton variant="rounded" height={40} sx={{ borderRadius: 1 }} />
      ) : (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(4, 1fr)',
            gap: 1,
            py: 0.75,
            px: 1,
            borderRadius: 1,
            backgroundColor: 'rgba(255,255,255,0.03)',
          }}
        >
          <Stat
            label="Net P/L"
            value={stats ? formatAlertProfit(stats.netProfit) : '—'}
            color={netColor}
          />
          <Stat label="Trades" value={stats ? stats.closedCount : '—'} />
          <Stat label="Win Rate" value={winRate} />
          <Stat label="Open" value={stats ? stats.openCount : '—'} />
        </Box>
      )}
    </Box>
  )
}

export default AccountPerformance
